/**
 * Per-field help for the Meta WhatsApp Cloud API provider form.
 *
 * Renders an info icon (reusing the shared HelpTooltip) that, on click, shows
 * WHERE in Meta each value comes from — the URL plus the steps to obtain it.
 * Meant to be dropped into a field's `InputProps.endAdornment`.
 *
 * Each field's text is SELF-CONTAINED: the full navigation path is repeated so
 * the reader never has to open another field's help. Content is bilingual
 * (es/en) and follows the UI locale.
 */

import React from "react";
import { Box, Link, Typography } from "@mui/material";
import { useLocaleState } from "react-admin";
import { HelpTooltip } from "@/components/common";

export type MetaHelpField =
  | "phoneNumberId"
  | "wabaId"
  | "accessToken"
  | "graphApiVersion"
  | "apiEndpoint"
  | "metaBusinessPhoneNumber";

type Locale = "es" | "en";

interface FieldHelp {
  title: string;
  steps: string[];
  url: string;
  note?: string;
}

const HELP: Record<MetaHelpField, Record<Locale, FieldHelp>> = {
  phoneNumberId: {
    es: {
      title: "Phone Number ID",
      steps: [
        "Entra en Meta for Developers y abre tu App.",
        "Ve a «Casos de uso» → WhatsApp (pulsa en «Personalizar») → «Configuración de la API».",
        "Copia el «Identificador del número de teléfono» (aparece bajo el número; no es el número en sí).",
      ],
      url: "https://developers.facebook.com/apps",
    },
    en: {
      title: "Phone Number ID",
      steps: [
        "Open Meta for Developers and select your App.",
        "Go to «Use cases» → WhatsApp (click on «Customize») → «API Setup».",
        "Copy the «Phone number ID» (shown under the number; it is not the number itself).",
      ],
      url: "https://developers.facebook.com/apps",
    },
  },
  wabaId: {
    es: {
      title: "WABA ID",
      steps: [
        "Entra en Meta for Developers y abre tu App.",
        "Ve a «Casos de uso» → WhatsApp (pulsa en «Personalizar») → «Configuración de la API».",
        "Copia el «Identificador de la cuenta de WhatsApp Business» que aparece en esa página.",
      ],
      url: "https://developers.facebook.com/apps",
    },
    en: {
      title: "WABA ID",
      steps: [
        "Open Meta for Developers and select your App.",
        "Go to «Use cases» → WhatsApp (click on «Customize») → «API Setup».",
        "Copy the «WhatsApp Business Account ID» shown on that page.",
      ],
      url: "https://developers.facebook.com/apps",
    },
  },
  accessToken: {
    es: {
      title: "Access Token",
      steps: [
        "En Configuración del negocio de Meta, ve a Usuarios → Usuarios del sistema.",
        "Crea o selecciona un usuario del sistema y asígnale tu App de WhatsApp / la WABA.",
        "Pulsa «Generar nuevo token», elige la App, marca los permisos whatsapp_business_messaging y whatsapp_business_management, pon la caducidad en «Nunca» y copia el token.",
      ],
      note: "El token de 24 h que aparece en «Configuración de la API» de WhatsApp solo sirve para pruebas. Deja el campo vacío para conservar el token actual.",
      url: "https://business.facebook.com/settings/system-users",
    },
    en: {
      title: "Access Token",
      steps: [
        "In Meta Business Settings, go to Users → System users.",
        "Create or select a system user and assign it your WhatsApp app / the WABA.",
        "Click «Generate new token», pick the App, tick the scopes whatsapp_business_messaging and whatsapp_business_management, set the expiry to «Never» and copy the token.",
      ],
      note: "The 24-hour token shown on the WhatsApp «API Setup» page is for testing only. Leave this field empty to keep the current token.",
      url: "https://business.facebook.com/settings/system-users",
    },
  },
  graphApiVersion: {
    es: {
      title: "Graph API Version",
      steps: [
        "No se obtiene de tu cuenta: es la versión de la Graph API con la que se integra.",
        "Usa una versión vigente, por ejemplo v25.0.",
        "Puedes ver las versiones disponibles en el changelog de la Graph API de Meta.",
      ],
      url: "https://developers.facebook.com/docs/graph-api/changelog",
    },
    en: {
      title: "Graph API Version",
      steps: [
        "Not taken from your account: it is the Graph API version the integration targets.",
        "Use a current version, for example v25.0.",
        "You can see the available versions in Meta's Graph API changelog.",
      ],
      url: "https://developers.facebook.com/docs/graph-api/changelog",
    },
  },
  apiEndpoint: {
    es: {
      title: "API Endpoint",
      steps: [
        "Valor fijo para la Cloud API de WhatsApp: https://graph.facebook.com",
        "Déjalo tal cual salvo que Meta indique lo contrario.",
      ],
      url: "https://developers.facebook.com/docs/whatsapp/cloud-api",
    },
    en: {
      title: "API Endpoint",
      steps: [
        "Fixed value for the WhatsApp Cloud API: https://graph.facebook.com",
        "Keep it as is unless Meta instructs otherwise.",
      ],
      url: "https://developers.facebook.com/docs/whatsapp/cloud-api",
    },
  },
  metaBusinessPhoneNumber: {
    es: {
      title: "Business Phone Number",
      steps: [
        "Entra en Meta for Developers y abre tu App.",
        "Ve a «Casos de uso» → WhatsApp (pulsa en «Personalizar») → «Configuración de la API».",
        "Copia el número de teléfono de WhatsApp Business que aparece ahí (junto al Identificador del número), con su prefijo internacional.",
      ],
      url: "https://developers.facebook.com/apps",
    },
    en: {
      title: "Business Phone Number",
      steps: [
        "Open Meta for Developers and select your App.",
        "Go to «Use cases» → WhatsApp (click on «Customize») → «API Setup».",
        "Copy the WhatsApp Business phone number shown there (next to the Phone number ID), including its country prefix.",
      ],
      url: "https://developers.facebook.com/apps",
    },
  },
};

interface MetaFieldHelpProps {
  field: MetaHelpField;
}

export const MetaFieldHelp: React.FC<MetaFieldHelpProps> = ({ field }) => {
  const [locale] = useLocaleState();
  const loc: Locale = locale === "en" ? "en" : "es";
  const h = HELP[field][loc];
  const linkLabel = loc === "en" ? "Open in Meta ↗" : "Abrir en Meta ↗";

  return (
    <HelpTooltip
      content={
        <Box sx={{ maxWidth: 360 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {h.title}
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.5 } }}>
            {h.steps.map((s, i) => (
              <Typography
                key={i}
                component="li"
                variant="body2"
                sx={{ lineHeight: 1.5, wordBreak: "break-word" }}
              >
                {s}
              </Typography>
            ))}
          </Box>
          {h.note && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1, lineHeight: 1.5 }}
            >
              {h.note}
            </Typography>
          )}
          <Link
            href={h.url}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            sx={{ display: "inline-block", mt: 1.25, wordBreak: "break-all" }}
          >
            {linkLabel}
          </Link>
        </Box>
      }
    />
  );
};

export default MetaFieldHelp;
